import { Component } from '@theme/component';

/**
 * Opens one The Making image at a time in a full-screen dialog.
 *
 * Each image is a button carrying `data-zoom-src`. Clicks are delegated from
 * this element, so it works for every layout without its own refs per image.
 * Inside the dialog the image first fits the screen uncropped; a click or tap
 * toggles 2x, centred on the point pressed, and the stage scrolls to pan.
 * Esc, the close button and a backdrop click all close it.
 */
class KpMakingZoom extends Component {
  requiredRefs = ['dialog', 'stage', 'image', 'close'];

  #abortController = new AbortController();
  /** @type {HTMLElement | null} */
  #opener = null;

  connectedCallback() {
    super.connectedCallback();

    const { dialog, stage, image, close } = this.refs;
    const { signal } = this.#abortController;

    this.addEventListener(
      'click',
      (event) => {
        const trigger = event.target instanceof Element ? event.target.closest('[data-zoom-src]') : null;
        if (trigger instanceof HTMLElement) this.#open(trigger);
      },
      { signal }
    );

    close.addEventListener('click', () => dialog.close(), { signal });

    // The stage fills the dialog, so a click outside the image lands on it
    stage.addEventListener(
      'click',
      (event) => {
        if (event.target === stage) dialog.close();
      },
      { signal }
    );

    image.addEventListener('click', (event) => this.#toggle(event), { signal });

    dialog.addEventListener(
      'close',
      () => {
        this.#reset();
        this.#opener?.focus({ preventScroll: true });
        this.#opener = null;
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {HTMLElement} trigger */
  #open(trigger) {
    const { dialog, image } = this.refs;

    this.#opener = trigger;
    this.#reset();
    image.alt = trigger.dataset.zoomAlt ?? '';
    image.src = trigger.dataset.zoomSrc ?? '';
    dialog.showModal();
  }

  /** @param {MouseEvent} event */
  #toggle(event) {
    const { stage, image } = this.refs;

    if (stage.dataset.zoomed) {
      this.#reset();
      return;
    }

    // Where the pointer sits on the fitted image, as a share of its size
    const box = image.getBoundingClientRect();
    const x = (event.clientX - box.left) / box.width;
    const y = (event.clientY - box.top) / box.height;

    stage.dataset.zoomed = 'true';
    image.style.width = `${box.width * 2}px`;

    stage.scrollLeft = x * image.offsetWidth - stage.clientWidth / 2;
    stage.scrollTop = y * image.offsetHeight - stage.clientHeight / 2;
  }

  #reset() {
    const { stage, image } = this.refs;

    delete stage.dataset.zoomed;
    image.style.removeProperty('width');
    stage.scrollTo(0, 0);
  }
}

if (!customElements.get('kp-making-zoom')) {
  customElements.define('kp-making-zoom', KpMakingZoom);
}
